/**
 * actionBridge.js — the seam between the AI copilot and the live app.
 *
 * App.js (and ProjectStartup) register a `getState` reader and a map of
 * `actions` here on every render, so the bridge always holds fresh closures
 * over the latest React state. The agent's tool layer (toolRegistry.js) reads
 * state and dispatches mutations *only* through this module — it never touches
 * React internals directly.
 *
 * Why a module-level singleton instead of context: the agent loop runs outside
 * the React tree (driven by async network round-trips to Gemini), so it needs a
 * stable, imperative handle it can call from anywhere. Between any two agent
 * actions there is a Gemini round-trip, which gives React time to flush and
 * re-register fresh closures — so sequential writes see up-to-date state.
 */

const bridge = {
  // Returns a snapshot: { project, analysisResults, selectedProcess,
  // leftViewMode, rightPanelMode, showStartup }. Default keeps callers safe
  // before App has mounted.
  getState: () => ({}),
  // name -> async (args) => observation
  actions: {},
};

/**
 * Register/refresh the bridge. Called from App.js on each render (cheap).
 * `actions` is merged so multiple surfaces (App, ProjectStartup) can each
 * contribute the actions they own without clobbering the others.
 */
export function registerBridge({ getState, actions } = {}) {
  if (typeof getState === 'function') bridge.getState = getState;
  if (actions && typeof actions === 'object') {
    bridge.actions = { ...bridge.actions, ...actions };
  }
}

/** Remove a set of action names (e.g. when a surface unmounts). */
export function unregisterActions(names = []) {
  for (const name of names) delete bridge.actions[name];
}

/** Current app state snapshot. */
export function getState() {
  try {
    return bridge.getState() || {};
  } catch {
    return {};
  }
}

/** True if an action with this name is currently registered. */
export function hasAction(name) {
  return typeof bridge.actions[name] === 'function';
}

/** Names of all registered actions (used by tool dispatch for diagnostics). */
export function listActions() {
  return Object.keys(bridge.actions);
}

/**
 * Invoke a registered action by name. Throws a descriptive error if the action
 * is not available so the agent loop can surface it as an observation.
 */
export async function callAction(name, args = {}) {
  const fn = bridge.actions[name];
  if (typeof fn !== 'function') {
    throw new Error(`Action "${name}" is not available right now.`);
  }
  return fn(args);
}

export default bridge;
